// Step 2 — the columns that choose the attribute set (prototype `workDecide`
// + `propsDecide`). A lookup column offers the properties of its MASTER, read
// from the server with their value counts (never typed, never enumerated in
// the browser); a column of this form with a value list decides by its own
// values. Above the value limit a column is offered disabled with the hint to
// decide by a grouping column instead.

import { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Database, Link2, Lock, X } from 'lucide-react'
import type { ColumnDef } from '../../txn/types'
import type { UdaDriver } from '../types'
import { MAX_DRIVERS, MAX_DRIVER_VALUES } from '../limits'
import { normalize } from '../rules'
import {
  columnByKey, decidable, driverLabel, driverPath, formOf, humanize, isStandard, lookupColumns,
  ORIGIN_TENANT, parseLookup, sameDriverSource, slugKey, valueListColumns, type SetupForm,
} from './model'
import { opForm, useSetup } from './state'
import { readMasterValues, type DriverDomain } from './useDomains'
import { HintBox } from './ui'
import { tw } from '../../testHooks'

const COUNT_BATCH = 4

interface MasterColumn { column: string; domain: DriverDomain | null; groupBy?: string }

/** The columns of one lookup's master with their value counts, read when the
 *  row is opened (a few at a time). */
function useMasterColumns(open: boolean, formNo: string, via: string, table: string,
  only: Array<{ column: string; group_by?: string }> | null) {
  const { transport, objName } = useSetup()
  const [cols, setCols] = useState<MasterColumn[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!open || cols) return undefined
    let cancelled = false
    void (async () => {
      try {
        let names: string[]
        if (only) names = only.map(a => a.column)
        else {
          const r = await transport.catalogColumns(objName, table)
          names = (r?.columns || []).map(c => c.column_name)
        }
        const list: MasterColumn[] = names.map(n => ({
          column: n, domain: null, groupBy: only?.find(a => a.column.toLowerCase() === n.toLowerCase())?.group_by,
        }))
        if (cancelled) return
        setCols(list)
        for (let i = 0; i < list.length; i += COUNT_BATCH) {
          const batch = list.slice(i, i + COUNT_BATCH)
          const doms = await Promise.all(batch.map(c => readMasterValues(transport, objName, formNo, via, c.column)))
          if (cancelled) return
          setCols(prev => (prev || []).map(c => {
            const k = batch.findIndex(b => b.column === c.column)
            return k >= 0 ? { ...c, domain: doms[k] } : c
          }))
        }
      } catch (err) {
        console.warn('[uda setup] the master columns could not be read', table, err)
        if (!cancelled) setError((err as Error)?.message || `The columns of ${table} could not be read`)
      }
    })()
    return () => { cancelled = true }
  }, [open, cols, transport, objName, formNo, via, table, only])
  return { cols, error }
}

function countText(dom: DriverDomain | null, grouped: boolean): string {
  if (!dom || dom.loading) return 'counting…'
  if (dom.error) return dom.error
  if (dom.total > MAX_DRIVER_VALUES) return `${dom.total.toLocaleString()} values: use a grouping column`
  if (!dom.total) return 'no values yet'
  return `${dom.total} values${grouped || dom.groups.length ? ' · grouped' : ''}`
}

function LookupSource({ col, side, formNo }: { col: ColumnDef; side: 'form' | 'header'; formNo: string }) {
  const { draft, mode } = useSetup()
  const lk = parseLookup(col)!
  const key = String(col.name || '').toLowerCase()
  const via = `${side}.${key}`
  const lf = formOf(draft.layer, formNo)
  const askBy = (draft.design?.forms?.[formNo]?.ask_by || []).filter(a => a.via.toLowerCase() === via)
  // The design layer's `ask_by` (when it names this column) is the list a
  // tenant may decide by; the designer sees every column.
  const onlyRaw = mode === 'tenant' && (draft.design?.forms?.[formNo]?.ask_by || []).length ? askBy : null
  const onlyKey = JSON.stringify(onlyRaw)
  // Stable per content, so reading the master's columns is not restarted on
  // every render.
  const only = useMemo(() => onlyRaw, [onlyKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const picked = (lf?.drivers || []).filter(d => d.from.kind === 'master' && d.from.via.toLowerCase() === via)
  const [open, setOpen] = useState(picked.length > 0)
  const { cols, error } = useMasterColumns(open, formNo, via, lk.table, only)
  const hidden = mode === 'tenant' && only !== null && !only.length
  if (hidden) return null
  return (
    <>
      <button type="button" className="uds-srow head" onClick={() => setOpen(o => !o)} aria-expanded={open}
        {...tw('action', `uda-source:${via}`)}>
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}<Link2 size={15} />
        <b>{col.heading || col.name}</b>
        <span className="m uds-mono">{col.name} → {lk.table}</span>
      </button>
      {open && !cols && !error && <div className="uds-srow sub disabled"><span className="uds-spin" /> Reading the master&apos;s columns…</div>}
      {open && error && <div className="uds-srow sub disabled" style={{ color: 'var(--color-error)' }}>{error}</div>}
      {open && cols?.length === 0 && <div className="uds-srow sub disabled">The master has no columns to decide by.</div>}
      {open && (cols || []).map(mc => (
        <DeciderToggle key={mc.column} formNo={formNo}
          from={{ kind: 'master', via, column: mc.column }} label={humanize(mc.column)}
          ok={decidable(mc.domain?.total)} meta={countText(mc.domain, !!mc.groupBy)} />
      ))}
    </>
  )
}

function DeciderToggle({ formNo, from, label, ok, meta }: {
  formNo: string; from: UdaDriver['from']; label: string; ok: boolean; meta: string
}) {
  const { draft, notify, additionsAllowed, go } = useSetup()
  const lf = formOf(draft.layer, formNo)
  const drivers = lf?.drivers || []
  const existing = drivers.find(d => sameDriverSource(d, from))
  const locked = !!existing && isStandard(existing) && draft.mode === 'tenant'
  const toggle = () => {
    if (existing) {
      if (locked) { notify('info', 'This deciding column comes with the application and cannot be removed.'); return }
      if (drivers.length === 1 && (lf?.sets || []).length) { notify('info', 'Keep at least one deciding column.'); return }
      const k = existing.key
      draft.edit(opForm(formNo, f => {
        f.drivers = f.drivers.filter(d => d.key !== k)
        for (const s of f.sets) {
          s.rules = normalize((s.rules || []).map(r => { const n = { ...r }; delete n[k]; return n }), f.drivers)
        }
      }))
      go({ t: 'decide', di: 0 })
      return
    }
    if (!additionsAllowed(formNo)) { notify('info', 'The application does not allow adding deciding columns here.'); return }
    if (drivers.length >= MAX_DRIVERS) { notify('info', `Up to ${MAX_DRIVERS} deciding columns.`); return }
    const key = slugKey(from.column, drivers.map(d => d.key), 'driver')
    draft.edit(opForm(formNo, f => {
      f.drivers.push({ key, label, from: { ...from }, origin: ORIGIN_TENANT })
      for (const s of f.sets) s.rules = (s.rules || []).map(r => ({ ...r, [key]: '*' }))
    }))
    go({ t: 'decide', di: drivers.length })
  }
  return (
    <label className={`uds-srow sub ${ok || existing ? '' : 'disabled'}`}>
      <input type="checkbox" checked={!!existing} disabled={(!ok && !existing) || locked} onChange={toggle}
        {...tw('action', `uda-decider:${from.kind === 'master' ? `${from.via}:${from.column}` : `form.${from.column}`}`.toLowerCase())} />
      {label}{locked && <Lock size={13} style={{ color: 'var(--color-text-faint)' }} />}
      <span className="m">{meta}</span>
    </label>
  )
}

function Sources({ title, form, side, formNo }: { title: string; form: SetupForm; side: 'form' | 'header'; formNo: string }) {
  const lookups = lookupColumns(form)
  const lists = side === 'form' ? valueListColumns(form) : []
  const others = side === 'form'
    ? form.columns.filter(c => !c.uda && !lookups.includes(c) && !lists.includes(c) && !c.hidden)
    : []
  if (!lookups.length && !lists.length && !others.length) return null
  return (
    <div className="uds-src">
      <div className="uds-src-h">{title}</div>
      {lookups.map(c => <LookupSource key={c.name} col={c} side={side} formNo={formNo} />)}
      {lists.map(c => (
        <DeciderToggle key={c.name} formNo={formNo} from={{ kind: 'form', column: String(c.name).toLowerCase() }}
          label={c.heading || humanize(c.name)} ok meta={`${(c.edit_mask?.values || []).length} values · on this form`} />
      ))}
      {others.map(c => (
        <div key={c.name} className="uds-srow disabled">{c.heading || c.name}<span className="m">free text</span></div>
      ))}
    </div>
  )
}

export default function StepDecidedBy() {
  const { forms, formNo, draft, node, go, domains } = useSetup()
  const form = forms.find(f => f.formNo === formNo)
  const parent = forms.find(f => f.formNo === form?.parentFormNo)
  const lf = formOf(draft.layer, formNo)
  const drivers = lf?.drivers || []
  const di = node.t === 'decide' ? node.di ?? 0 : 0
  const move = (i: number, d: -1 | 1) => {
    const j = i + d
    if (j < 0 || j >= drivers.length) return
    if (draft.mode === 'tenant' && (isStandard(drivers[i]) || isStandard(drivers[j]))) return
    const a = drivers[i].key
    const b = drivers[j].key
    draft.edit(opForm(formNo, f => {
      const ia = f.drivers.findIndex(x => x.key === a)
      const ib = f.drivers.findIndex(x => x.key === b)
      if (ia < 0 || ib < 0) return
      const t = f.drivers[ia]; f.drivers[ia] = f.drivers[ib]; f.drivers[ib] = t
    }))
    go({ t: 'decide', di: j })
  }
  if (!form) return null
  return (
    <>
      <div className="uds-lead">
        <h2>2. Decide the columns that choose the attribute set</h2>
        <p>Pick up to {MAX_DRIVERS === 3 ? 'three' : MAX_DRIVERS} columns. A column with a lookup lets you choose a property of its master, so
          the value does not need to be on the screen. Values are read from the master, never typed. A column can
          have up to {MAX_DRIVER_VALUES} values; for more, decide by a grouping column (a category, a type) instead.</p>
      </div>
      <div className="uds-picker">
        <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
          {parent && <Sources title={`From the ${parent.title.toLowerCase()}`} form={parent} side="header" formNo={formNo} />}
          <Sources title={`On ${form.title.toLowerCase()}`} form={form} side="form" formNo={formNo} />
        </div>
        <div className="uds-card" style={{ alignSelf: 'start' }}>
          <div className="uds-card-h"><h3>Decided by, in this order</h3><p>The order is the column order of the Combinations table.</p></div>
          <div className="uds-card-b">
            {drivers.length ? drivers.map((d, i) => {
              const dom = domains.domainOf(formNo, d)
              const std = isStandard(d) && draft.mode === 'tenant'
              return (
                <div key={d.key} className={`uds-decider ${di === i ? 'on' : ''}`}>
                  <button type="button" className="uds-icbtn" aria-label="Move up" disabled={std || i === 0} onClick={() => move(i, -1)}><ArrowUp size={15} /></button>
                  <button type="button" className="uds-icbtn" aria-label="Move down" disabled={std || i === drivers.length - 1} onClick={() => move(i, 1)}><ArrowDown size={15} /></button>
                  <button type="button" className="uds-grow" style={{ border: 0, background: 'transparent', textAlign: 'left', padding: 0, cursor: 'pointer', minWidth: 0 }}
                    onClick={() => go({ t: 'decide', di: i })}>
                    <span className="t">{driverLabel(d)}</span>{std && <> <Lock size={12} /></>}
                    <span className="p">{driverPath(d)}</span>
                  </button>
                  <span className="uds-pill uds-p-std">{dom.loading ? '…' : `${dom.total} values`}</span>
                </div>
              )
            }) : <div className="uds-na">Nothing chosen yet. Tick a column on the left.</div>}
          </div>
        </div>
      </div>
      <div>
        <button type="button" className="btn btn-primary" disabled={!drivers.length} onClick={() => go({ t: 'combos' })}>
          Next: combinations <ChevronRight size={15} />
        </button>
      </div>
    </>
  )
}

export function DeciderPanel() {
  const { forms, formNo, draft, node, domains, notify, go } = useSetup()
  const lf = formOf(draft.layer, formNo)
  const drivers = lf?.drivers || []
  const i = node.t === 'decide' ? node.di ?? 0 : 0
  const d = drivers[i]
  const form = forms.find(f => f.formNo === formNo)
  const values = useMemo(() => (d ? domains.domainOf(formNo, d) : null), [d, domains, formNo])
  if (!d) return <div className="uds-pbody"><div className="uds-na">Tick a column in the work area to decide by it.</div></div>
  const std = isStandard(d) && draft.mode === 'tenant'
  let through = ''
  let viaCol: ColumnDef | null = null
  let header = false
  if (d.from.kind === 'master') {
    const [side, col] = d.from.via.split('.')
    header = side === 'header'
    const host = header ? forms.find(f => f.formNo === form?.parentFormNo) : form
    viaCol = columnByKey(host || null, col)
    through = String(viaCol?.lookup || '')
  }
  const remove = () => {
    if (std) return
    if (drivers.length === 1 && (lf?.sets || []).length) { notify('info', 'Keep at least one deciding column.'); return }
    const k = d.key
    draft.edit(opForm(formNo, f => {
      f.drivers = f.drivers.filter(x => x.key !== k)
      for (const s of f.sets) s.rules = normalize((s.rules || []).map(r => { const n = { ...r }; delete n[k]; return n }), f.drivers)
    }))
    go({ t: 'decide', di: 0 })
  }
  return (
    <>
      <div className="uds-props-h">
        <div className="row"><span className="uds-pill uds-p-acc">Deciding column</span>{std && <span className="uds-pill uds-p-std">Standard</span>}</div>
        <h2>{driverLabel(d)}</h2>
        <div className="sub">{driverPath(d)}</div>
      </div>
      <div className="uds-pbody">
        <label className="uds-lbl" htmlFor="uds-dlabel">Shown as</label>
        <input id="uds-dlabel" className="uds-inp" value={d.label || ''} disabled={std} placeholder={driverLabel(d)}
          onChange={e => { const v = e.target.value; const k = d.key; draft.edit(opForm(formNo, f => { const x = f.drivers.find(y => y.key === k); if (x) x.label = v })) }} />
        {d.from.kind === 'master' ? (
          <>
            <label className="uds-lbl">Reached through</label>
            <div className="uds-inp uds-mono" style={{ display: 'flex', alignItems: 'center' }}>{through || d.from.via}</div>
            <div className="uds-hint">
              The lookup on {viaCol?.heading || d.from.via}{header ? ' (the header)' : ''}. When it changes, the value is read
              again and the attribute set is worked out again.
            </div>
          </>
        ) : (
          <div className="uds-hint">A column of this form; its values are the column&apos;s own value list.</div>
        )}
        <label className="uds-lbl">Values {d.from.kind === 'master' ? 'in the master' : 'of the column'}</label>
        {values?.loading && <div className="uds-hint"><span className="uds-spin" /> Reading…</div>}
        {values?.error && <div className="uds-hint" style={{ color: 'var(--color-error)' }}>{values.error}</div>}
        <div>
          {(values?.values || []).map(([c, l, n]) => (
            <div key={c} className="uds-li">
              <span className="t">{l} {l !== c && <span className="uds-mono" style={{ color: 'var(--color-text-faint)' }}>{c}</span>}</span>
              {n != null && <span style={{ color: 'var(--color-text-faint)' }}>{n.toLocaleString()}</span>}
            </div>
          ))}
          {values?.truncated && <div className="uds-hint">More than {MAX_DRIVER_VALUES} values — decide by a grouping column instead.</div>}
        </div>
        <HintBox icon={<Database size={15} />}>
          On save, the server reads this value from the master again, so a changed {driverLabel(d).toLowerCase()} never keeps the wrong attributes.
        </HintBox>
        {!std && (
          <div style={{ marginTop: 14 }}>
            <button type="button" className="btn btn-secondary btn-sm uds-danger" onClick={remove}><X size={14} /> Stop deciding by it</button>
          </div>
        )}
      </div>
    </>
  )
}
